import React, { useContext, useState } from "react";
import { MediaQueriesContext } from "@/App.jsx";
import { useHistory, useLocation } from "react-router-dom";
import Button from "Components/button/Button";
import ListControls from "./components/listControls/ListControls";
import DeliveryAccountCard from "Components/deliveryAccountCard/DeliveryAccountCard";
import DeliveryAccountRow from "Components/deliveryAccountRow/DeliveryAccountRow";
import Loader from "Components/loader/Loader";
import { Icon, plus, globe } from "Icons/index";
import s from "../delivery.scss";
import t from "Utils/translation";
import cx from "classnames";
import EmptyListIcon from "@/components/emptyListIcon/EmptyListIcon";
import useQueryParams from "@/utils/useQueryParams";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import { AuthContext } from "@/App";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import _ from "lodash";
import useLocalStorage from "Utils/useLocalStorage";
import ConfirmModal from "Components/confirmModal/ConfirmModal";
import { useApiMutation, useApiQuery } from "../../../hooks";

const DeliveryAccountsListScreen = () => {
  const history = useHistory();
  const media = useContext(MediaQueriesContext);
  const location = useLocation();
  const queryParams = useQueryParams();
  const { isLoggedUserAdmin, getLoggedUser } = useContext(AuthContext);

  const loggedUser = getLoggedUser();

  const [isForbiddenModalOpen, setIsForbiddenModalOpen] = useState(false);

  const [listLayout, setListLayout] = useLocalStorage(`qb_delivery_list_layout_${loggedUser.code}`, 0);

  const addNew = () => history.push("/delivery/accounts/new");

  const {
    data: accounts,
    isLoading,
    refreshData: refreshAccounts,
    isRefreshing,
    pagination,
    updateData: updateAccountsData,
  } = useApiQuery("getDeliveryAccounts", {
    enablePagination: true,
  });

  const { mutate: updateDeliveryAccountStatus, isLoading: isUpdatingAccountStatus } = useApiMutation(
    "changeDeliveryAccountStatus",
    {
      optimistic: (id, newStatus) => {
        updateAccountsData((prev) => {
          return prev.map((account) => {
            if (account.id === id) return { ...account, updating: true };
            return account;
          });
        });
      },
    }
  );

  const updateAccountStatus = async (id, status) => {
    const canEditStatus = !(
      loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("edit_delivery_account_status")
    );

    if (!canEditStatus) return setIsForbiddenModalOpen(true);

    try {
      await updateDeliveryAccountStatus(id, status);
    } catch (error) {
      console.error("Unable to update account: Something went wrong");
      console.error(error);
    } finally {
      refreshAccounts();
    }
  };

  let filters = ["all", "enabled", "disabled"];

  const goToAccountDetails = (accountData) => {
    if (isLoggedUserAdmin()) {
      history.push({
        pathname: `/customers/${accountData.customer_code}/delivery/accounts/${accountData.id}`,
        state: { fromList: true },
      });
    } else {
      history.push({ pathname: `/delivery/accounts/${accountData.id}`, state: { fromList: true } });
    }
  };

  const canCreateAccount = !(
    loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("create_delivery_accounts")
  );

  return (
    <div className="screenContainer">
      <div className={s.listHeader}>
        <div>
          <div className="sectionTitle">{t("delivery.accounts.listTitle")}</div>
          {!media.isMediaMobile && <div className="sectionDesc marginRight80">{t("delivery.accounts.listDesc")}</div>}
        </div>
        <div className={s.actionsList}>
          <Button docsKey="deliveryAccounts" color="docs" className={s.btnList} onlyIcon={media.isMediaMobile}>
            button.docs
          </Button>
          {canCreateAccount && (
            <Button icon={plus} onClick={addNew} onlyIcon={media.isMediaMobile}>
              delivery.accounts.button.addNew
            </Button>
          )}
        </div>
      </div>

      <ConfirmModal
        title={t("teamMembers.forbiddenAction")}
        text={t("teamMembers.forbiddenActionSubtitle")}
        textButton="button.ok"
        hideCancelButton={true}
        opened={isForbiddenModalOpen}
        action={() => setIsForbiddenModalOpen(false)}
        handleClose={() => setIsForbiddenModalOpen(false)}
        buttonColor="accent"
      />

      <ListControls
        className={s.filters}
        paginationInfo={pagination || {}}
        location={location}
        history={history}
        refreshList={refreshAccounts}
        listFilters={filters}
        listFilterLegends={{
          all: <Icon svg={globe} width="0.75rem" height="0.75rem" />,
          enabled: <DomainStatusBadge status="enabled" isHealthy showAsPreview />,
          disabled: <DomainStatusBadge status="disabled" isHealthy showAsPreview />,
        }}
        showCustomerSelection={isLoggedUserAdmin()}
        isLoading={isLoading}
        totals={pagination && pagination.totals}
        selectedListLayout={listLayout}
        onListLayoutChange={setListLayout}
      />
      <div className={s.contentList}>
        {listLayout === 0 ? (
          <div className={s.listContainer}>
            <ResponsiveTable
              history={history}
              location={location}
              areRowsLoading={!accounts || (isLoading && !isRefreshing)}
              addMsg={t("infoMsg.noRecords.msg")}
              addDesc={t("infoMsg.noRecords.desc")}
              headers={_.compact([
                {
                  name: "Status",
                  size: media.isMobileDevice ? "2rem" : "4rem",
                  center: true,
                },
                {
                  name: t("table.header.delivery.account"),
                  sortable: true,
                  key: "description",
                  activeSorting: queryParams.get("sort_field") === "description" && queryParams.get("sort"),
                },
                isLoggedUserAdmin()
                  ? {
                      name: t("delivery.accounts.label.customerName"),
                    }
                  : {
                      name: t("table.header.delivery.account.username"),
                      hideOnMobile: true,
                      sortable: true,
                      key: "username",
                      activeSorting: queryParams.get("sort_field") === "username" && queryParams.get("sort"),
                    },
                {
                  name: t("delivery.accounts.label.type"),
                  sortable: true,
                  hideOnMobile: true,
                  key: "type",
                  activeSorting: queryParams.get("sort_field") === "type" && queryParams.get("sort"),
                },
                {
                  name: t("delivery.accounts.label.plan"),
                  sortable: true,
                  hideOnMobile: true,
                  key: "plan",
                  activeSorting: queryParams.get("sort_field") === "plan" && queryParams.get("sort"),
                },
                { name: t("delivery.accounts.label.availableMessages"), hideOnMobile: isLoggedUserAdmin() },
                {
                  name: t("delivery.accounts.label.domainsCount"),
                  hideOnMobile: true,
                  sortable: false,
                },
                { name: t("delivery.accounts.label.renewalDate"), hideOnMobile: isLoggedUserAdmin() },
                { size: "3rem", center: true },
                { size: "3rem", center: true },
              ])}
              rows={
                accounts
                  ? accounts.map((accountData) => (
                      <DeliveryAccountRow
                        key={accountData.id}
                        accountData={accountData}
                        onClick={() => goToAccountDetails(accountData)}
                        isStatusUpdating={accountData.updating}
                        onStatusChangeButtonClick={() =>
                          updateAccountStatus(accountData.id, accountData.status ? "disabled" : "enabled")
                        }
                        statusChangeButtonLabel={accountData.status ? t("ddMenu.disable") : t("ddMenu.enable")}
                      />
                    ))
                  : []
              }
            />
          </div>
        ) : (
          <>
            {/* No records case */}
            {accounts && !isLoading && accounts.length === 0 && (
              <div className="mt-16">
                {[...queryParams.entries()].length > 0 ? (
                  <EmptyListIcon isNotFound title={t("infoMsg.noRecords.msg")} message={t("infoMsg.noRecords.desc")} />
                ) : (
                  <EmptyListIcon message={t("delivery.accounts.emptyList.noRecordsMsg")} />
                )}
              </div>
            )}

            {!accounts || (isLoading && !isRefreshing) ? (
              <div className="mt-8">
                <Loader />
              </div>
            ) : (
              <div className={cx(s.cardListContainer, "pb-20")}>
                {accounts.map((accountData) => (
                  <DeliveryAccountCard
                    key={accountData.id}
                    onClick={() => goToAccountDetails(accountData)}
                    isStatusUpdating={accountData.updating}
                    accountData={accountData}
                    onStatusChangeButtonClick={() =>
                      updateAccountStatus(accountData.id, accountData.status ? "disabled" : "enabled")
                    }
                    statusChangeButtonLabel={accountData.status ? t("ddMenu.disable") : t("ddMenu.enable")}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DeliveryAccountsListScreen;
